import { useState, useRef, useEffect, useMemo } from 'react';
import { accountDbApi } from '../shared/api-client';
import type { AccountSummary, ChatAccountAssociationStatus, ChatThreadAccountRef } from '../shared/api-client';
import './ChatAccountSelectorComponent.css';

export interface ChatAccountSelectorValue {
  accountId: string | null;
  status: ChatAccountAssociationStatus;
}

interface ChatAccountSelectorProps {
  value: ChatAccountSelectorValue;
  onChange: (next: ChatAccountSelectorValue) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  /** Initial label hint when value.status === 'set' but the local list hasn't loaded yet. */
  initialAccountHint?: ChatThreadAccountRef | null;
}

// Module-level cache: ~50 accounts are stable for the session.
let cachedAccountsPromise: Promise<AccountSummary[]> | null = null;
function loadAccounts(): Promise<AccountSummary[]> {
  if (cachedAccountsPromise) return cachedAccountsPromise;
  const promise = accountDbApi.getAccounts({ limit: 500 }).then((r) => {
    if (!r.success) {
      cachedAccountsPromise = null; // allow retry on next mount
      return [] as AccountSummary[];
    }
    return r.data.items;
  });
  cachedAccountsPromise = promise;
  return promise;
}

/** Subsequence fuzzy match: every char of `query` appears in `target` in order. Case-insensitive. */
function fuzzyMatches(query: string, target: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  const t = target.toLowerCase();
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  return qi === q.length;
}

const NO_CLIENT_VALUE: ChatAccountSelectorValue = { accountId: null, status: 'none' };

export function ChatAccountSelectorComponent({
  value,
  onChange,
  disabled,
  required,
  placeholder,
  initialAccountHint,
}: ChatAccountSelectorProps): JSX.Element {
  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    void loadAccounts().then((list) => {
      if (!cancelled) {
        setAccounts(list);
        setAccountsLoaded(true);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const selectedAccount = useMemo(() => {
    if (value.status !== 'set' || !value.accountId) return null;
    const fromList = accounts.find((a) => a.id === value.accountId);
    if (fromList) return { id: fromList.id, orgName: fromList.orgName, ref: fromList.ref };
    return initialAccountHint ?? null;
  }, [accounts, value, initialAccountHint]);

  const displayLabel = useMemo(() => {
    if (value.status === 'set') return selectedAccount?.orgName ?? (accountsLoaded ? '(unknown account)' : 'Loading…');
    if (value.status === 'none') return 'No Client';
    return '';
  }, [value.status, selectedAccount, accountsLoaded]);

  const filtered = useMemo(() => {
    if (!query.trim()) return accounts;
    return accounts.filter((a) =>
      fuzzyMatches(query, a.orgName ?? '')
      || (a.ref && fuzzyMatches(query, a.ref))
      || fuzzyMatches(query, a.id),
    );
  }, [accounts, query]);

  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  function pickAccount(a: AccountSummary): void {
    onChange({ accountId: a.id, status: 'set' });
    setIsOpen(false);
    setQuery('');
  }

  function pickNoClient(): void {
    onChange(NO_CLIENT_VALUE);
    setIsOpen(false);
    setQuery('');
  }

  function handleKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'Escape') { setIsOpen(false); setQuery(''); inputRef.current?.blur(); }
    if (e.key === 'Enter' && filtered.length === 1 && filtered[0]) { e.preventDefault(); pickAccount(filtered[0]); }
  }

  const showRequiredHint = required && value.status === 'unknown';
  const triggerClass = `chat-account-selector-trigger${disabled ? ' chat-account-selector-trigger--disabled' : ''}${showRequiredHint ? ' chat-account-selector-trigger--required' : ''}`;
  const placeholderText = placeholder ?? (required ? 'Pick a customer or "No Client" to begin' : 'Tag this chat with a customer (optional)');

  return (
    <div ref={containerRef} className="chat-account-selector">
      <button
        type="button"
        className={triggerClass}
        onClick={() => { if (!disabled) { setIsOpen((o) => !o); setTimeout(() => inputRef.current?.focus(), 0); } }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="material-icons chat-account-selector-icon">{value.status === 'set' ? 'business' : value.status === 'none' ? 'person_off' : 'help_outline'}</span>
        <span className={`chat-account-selector-label${value.status === 'unknown' ? ' chat-account-selector-label--placeholder' : ''}`}>
          {displayLabel || placeholderText}
        </span>
        <span className="material-icons chat-account-selector-chevron">expand_more</span>
      </button>
      {isOpen && (
        <div className="chat-account-selector-menu" role="listbox">
          <div className="chat-account-selector-search">
            <input
              ref={inputRef}
              className="chat-account-selector-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search customers…"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <ul className="chat-account-selector-list">
            {!accountsLoaded && (
              <li className="chat-account-selector-loading">Loading customers…</li>
            )}
            {accountsLoaded && filtered.length === 0 && (
              <li className="chat-account-selector-empty">No matches</li>
            )}
            {filtered.map((a) => (
              <li
                key={a.id}
                className={`chat-account-selector-option${value.accountId === a.id ? ' chat-account-selector-option--selected' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); pickAccount(a); }}
                role="option"
                aria-selected={value.accountId === a.id}
              >
                <span className="chat-account-selector-option-name">{a.orgName ?? '(unnamed)'}</span>
                {(a.ref || a.parentOrgName) && (
                  <span className="chat-account-selector-option-meta">
                    {[a.ref, a.parentOrgName].filter(Boolean).join(' · ')}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <div className="chat-account-selector-divider" />
          <button
            type="button"
            className={`chat-account-selector-no-client${value.status === 'none' ? ' chat-account-selector-no-client--selected' : ''}`}
            onMouseDown={(e) => { e.preventDefault(); pickNoClient(); }}
          >
            <span className="material-icons">person_off</span>
            <span>No Client</span>
          </button>
        </div>
      )}
    </div>
  );
}

/** Reset the module-level account cache. Call after creating a new account etc. */
export function resetAccountSelectorCache(): void {
  cachedAccountsPromise = null;
}
